// Floating DOM ghost rendered while the user is dragging a sticky out of the
// sidebar. Lives outside the Konva stage so it can ride the cursor even
// before pointermove events reach the canvas. Visual is a low-opacity
// sticky tile sized to match the world-space sticky that will land on drop;
// we multiply the world dimensions by the current viewport scale so the
// ghost matches the canvas zoom level.
//
// Pointer events are disabled on the ghost so the parent pointerup handler
// still receives drop events even when the ghost is directly under the
// cursor (we never want the ghost to capture the click).

import type { StickyColor } from '@frame/shared';
import { WIDE_STICKY_H, WIDE_STICKY_W, DEFAULT_STICKY_H, DEFAULT_STICKY_W } from './tools';
import { useTokens } from '../styles/theme';

interface Props {
  variant: 'square' | 'wide';
  color: StickyColor;
  screenX: number;
  screenY: number;
  scale: number;
}

export function StickyDragGhost({ variant, color, screenX, screenY, scale }: Props) {
  const tokens = useTokens();
  const worldW = variant === 'wide' ? WIDE_STICKY_W : DEFAULT_STICKY_W;
  const worldH = variant === 'wide' ? WIDE_STICKY_H : DEFAULT_STICKY_H;
  // Clamp scale so a deeply zoomed-out board still shows a visible ghost.
  const ghostScale = Math.max(0.4, Math.min(1.4, scale));
  const w = worldW * ghostScale;
  const h = worldH * ghostScale;
  return (
    <div
      aria-hidden
      style={{
        position: 'fixed',
        left: screenX - w / 2,
        top: screenY - h / 2,
        width: w,
        height: h,
        background: tokens.sticky[color],
        boxShadow: `0 ${4 * ghostScale}px ${12 * ghostScale}px rgba(0,0,0,0.18)`,
        borderRadius: 4,
        opacity: 0.85,
        pointerEvents: 'none',
        zIndex: 50,
        transform: 'translate3d(0,0,0)',
      }}
    />
  );
}
